import { useCallback, useEffect, useState } from 'react';

import { useGeolocation } from '@fhs-legacy/frontend/src/state/geolocation';

interface UseRequestLocationPermissionOptions {
  /** Fires once permission settles as granted after a request made through this hook. */
  onGranted?: () => void;
}

/**
 * Shared "ask for location" flow behind both the map's locate-me button and the empty
 * state's "Enable location services" link.
 *
 * `requestLocation` always re-runs the permission check (rather than short-circuiting
 * straight to the in-app modal when we already believe it's denied), so a user gesture can
 * surface the browser/OS prompt whenever the platform still allows it — a no-op if it's truly
 * blocked, in which case this falls back to the "Turn On Location Services" modal once the
 * check settles.
 */
export function useRequestLocationPermission({
  onGranted,
}: UseRequestLocationPermissionOptions = {}) {
  const {
    isPermissionDenied,
    isPermissionGranted,
    isPermissionChecking,
    isLoadingPermission,
    setPermissionChecking,
  } = useGeolocation();

  const [isDeniedModalOpen, setIsDeniedModalOpen] = useState(false);
  const [hasRequestedLocation, setHasRequestedLocation] = useState(false);

  const requestLocation = useCallback(() => {
    setHasRequestedLocation(true);
    if (!isPermissionChecking && !isLoadingPermission) {
      setPermissionChecking();
    }
  }, [isLoadingPermission, isPermissionChecking, setPermissionChecking]);

  const closeDeniedModal = useCallback(() => {
    setIsDeniedModalOpen(false);
    setHasRequestedLocation(false);
  }, []);

  // Once granted, always drop the modal — permission can change outside this flow too (e.g.
  // granted from the browser's own address bar UI), and a stale modal shouldn't linger.
  // `onGranted` stays scoped to hasRequestedLocation so it only fires for a grant that actually
  // resolved a request made through this hook (e.g. not on mount for a guest who granted access
  // in an earlier session, before ever tapping anything here).
  useEffect(() => {
    if (isPermissionGranted) {
      setIsDeniedModalOpen(false);

      if (hasRequestedLocation) {
        setHasRequestedLocation(false);
        onGranted?.();
      }
      return;
    }

    // After the browser/OS prompt settles, surface the modal on a deny.
    if (!hasRequestedLocation || isPermissionChecking || isLoadingPermission) {
      return;
    }

    if (isPermissionDenied) {
      setIsDeniedModalOpen(true);
    }
  }, [
    hasRequestedLocation,
    isLoadingPermission,
    isPermissionChecking,
    isPermissionDenied,
    isPermissionGranted,
    onGranted,
  ]);

  return {
    isDeniedModalOpen,
    closeDeniedModal,
    requestLocation,
    isPermissionDenied,
    isPermissionGranted,
    isPermissionChecking,
    isLoadingPermission,
  };
}
